// Static-only API service - Uses JSON files directly without backend calls
import axios from 'axios';

// Create axios instance for static files
const staticClient = axios.create({
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Static data fallback - loads JSON files directly
const loadStaticData = async (endpoint) => {
  try {
    // Try primary location first
    const response = await fetch(`/${endpoint}.json`);
    if (response.ok) {
      return await response.json();
    }
    
    // Try backup location
    const backupResponse = await fetch(`/api/${endpoint}.json`);
    if (backupResponse.ok) {
      return await backupResponse.json();
    }
    
    throw new Error(`Failed to load ${endpoint} data from both locations`);
  } catch (error) {
    console.error(`Failed to load static ${endpoint}.json:`, error);
    throw error;
  }
};

// Contact API - Demo mode only
export const contactAPI = {
  submitContactForm: async (contactData) => {
    // Simulate successful submission for demo
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: "Thank you for your message! We'll get back to you within 24 hours. (Note: This is a demo - please contact us directly at info@saxpms.com)"
        });
      }, 1000);
    });
  }
};

// Service Request API - Demo mode only
export const serviceRequestAPI = {
  submitServiceRequest: async (requestData, files = []) => {
    // Generate demo reference ID
    const refId = `SAX-DEMO-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
    
    // Simulate successful submission
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          reference_id: refId,
          message: `Service request submitted successfully! Your reference ID is ${refId}. (Note: This is a demo - please contact us directly at info@saxpms.com for actual service requests)`
        });
      }, 1000);
    });
  },
  
  getServiceRequest: async (referenceId) => {
    return {
      success: false,
      error: "Service request lookup not available in demo mode. Please contact us directly."
    };
  }
};

// Projects API - Static data only
export const projectsAPI = {
  getAllProjects: async () => {
    try {
      return await loadStaticData('projects');
    } catch (error) {
      console.error('Failed to load projects:', error);
      // Return empty data structure if loading fails
      return {
        success: false,
        projects: [],
        error: 'Failed to load projects data'
      };
    }
  },

  getProjectById: async (projectId) => {
    try {
      const staticData = await loadStaticData('projects');
      const project = staticData.projects?.find(p => p.id === projectId);
      return project ? { success: true, project } : { success: false, error: 'Project not found' };
    } catch (error) {
      console.error('Failed to load project:', error);
      return { success: false, error: 'Failed to load project data' };
    }
  }
};

// Staff API - Static data only
export const staffAPI = {
  getAllStaff: async () => {
    try {
      return await loadStaticData('staff');
    } catch (error) {
      console.error('Failed to load staff:', error);
      // Return empty data structure if loading fails
      return {
        success: false,
        staff: [],
        error: 'Failed to load staff data'
      };
    }
  },

  getStaffById: async (staffId) => {
    try {
      const staticData = await loadStaticData('staff');
      const staff = staticData.staff?.find(s => s.id === staffId);
      return staff ? { success: true, staff } : { success: false, error: 'Staff member not found' };
    } catch (error) {
      console.error('Failed to load staff member:', error);
      return { success: false, error: 'Failed to load staff data' };
    }
  }
};

// Admin API - Demo data
export const adminAPI = {
  getStats: async () => {
    return {
      success: true,
      total_contacts: 150,
      total_service_requests: 45,
      new_contacts: 12,
      new_service_requests: 3
    };
  }
};

// Health check - Static mode
export const healthCheck = async () => {
  return { 
    message: "SAX Website - Static Mode", 
    status: "static",
    timestamp: new Date().toISOString()
  };
};

export default {
  contactAPI,
  serviceRequestAPI,
  projectsAPI,
  staffAPI,
  adminAPI,
  healthCheck
};